<script setup lang="ts">
import PlanGateModal from '@/components/billing/PlanGateModal.vue';
import MiloSupportWidget from '@/components/common/MiloSupportWidget.vue';
import PwaUpdatePrompt from '@/components/common/PwaUpdatePrompt.vue';
import SyncQueueIndicator from '@/components/common/SyncQueueIndicator.vue';
import ToastContainer from '@/components/toast/ToastContainer.vue';
import ProgressBar from '@/components/ui/ProgressBar.vue';
import { useAuth } from '@/composables/useAuth';
import { useNetworkStatus } from '@/composables/useNetworkStatus';
import { RouteNames } from '@/router/names.ts';
import { getRouteViewKey } from '@/router/viewKey';
import { useSyncQueueStore } from '@/stores/syncQueue';
import { useToastStore } from '@/stores/toast';
import { log } from '@/utils/log';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import SidebarProvider from './components/layout/SidebarProvider.vue';
import ThemeProvider from './components/layout/ThemeProvider.vue';

const { isOnline } = useNetworkStatus();
const { user, isProfileCompleted, isAuthenticated } = useAuth();
const toast = useToastStore();
const syncQueue = useSyncQueueStore();
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const routeViewKey = computed(() => getRouteViewKey(route));

// An authenticated user who hasn't completed onboarding (no first company yet).
const showProfileIncompleteBanner = computed(() => {
    return isAuthenticated.value && user.value && isProfileCompleted.value === false;
});

// Onboarding gate: such users must create their first company before using the
// app. This is reactive (not a one-shot onMounted check) because the me-payload
// loads asynchronously after boot — a single check can run before the user is
// known and miss the redirect entirely.
const redirectToOnboarding = (): void => {
    // Don't pull users off public pages, and never loop on the create page itself.
    if (route.meta?.public || route.name === RouteNames.SettingsCompanyCreate) return;
    const redirectPath = route.query.redirect as string | undefined;
    router.replace({
        name: RouteNames.SettingsCompanyCreate,
        ...(redirectPath && { query: { redirect: redirectPath } }),
    });
};

watch(
    showProfileIncompleteBanner,
    (incomplete) => {
        if (incomplete) redirectToOnboarding();
    },
    { immediate: true },
);

// Sync app locale to the authenticated user's preferred language
watch(
    () => user.value?.preferred_lang,
    (preferredLang) => {
        if (preferredLang && preferredLang !== locale.value) {
            locale.value = preferredLang;
            localStorage.setItem('locale', preferredLang);
        }
    },
    { immediate: true },
);

// Keep <html lang> in sync with the active locale (WCAG 3.1.1/3.1.2). index.html
// hard-codes lang="nl", so without this an English visitor gets English content
// declared as Dutch — screen readers then mispronounce it.
watch(locale, (activeLocale) => document.documentElement.setAttribute('lang', String(activeLocale)), {
    immediate: true,
});

// Watch for network status changes and show centralized toast notifications
watch(isOnline, (online, wasOnline) => {
    // Skip initial value when component mounts
    if (wasOnline === undefined) return;

    if (online) {
        toast.success(t('network.online'));
        setTimeout(() => {
            toast.clear();
        }, 3000);
    } else {
        toast.warning(t('network.offline'), 0);
    }
});

// Listen for service worker sync messages
const handleSwSyncQueue = () => {
    log('Service worker requested queue sync');
    syncQueue.processQueue();
};

const handleServiceWorkerMessage = (event: MessageEvent) => {
    if (event.data?.type === 'SYNC_QUEUE') {
        log('Received SYNC_QUEUE message from service worker');
        window.dispatchEvent(new CustomEvent('sw-sync-queue'));
    }
};

onMounted(() => {
    window.addEventListener('sw-sync-queue', handleSwSyncQueue);

    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
});

onUnmounted(() => {
    window.removeEventListener('sw-sync-queue', handleSwSyncQueue);

    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    }
});

log('Network status:', isOnline.value);
</script>

<template>
    <ThemeProvider>
        <ProgressBar />
        <SidebarProvider>
            <RouterView v-slot="{ Component }">
                <Transition name="fade" mode="out-in">
                    <component :is="Component" :key="routeViewKey" />
                </Transition>
            </RouterView>
            <ToastContainer />
            <SyncQueueIndicator v-if="isAuthenticated" />
            <PwaUpdatePrompt />
            <PlanGateModal />
            <MiloSupportWidget :authenticated="isAuthenticated" />
        </SidebarProvider>
    </ThemeProvider>
</template>
